import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLoading } from '../context/LoadingContext';
import {
  ArrowLeftIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import '../styles/scrollbar.css';

// Types
interface GeneratedImage {
  url: string;
  prompt: string;
  timestamp: Date;
}

// Components
const Header: React.FC<{
  showHistory: boolean;
  onToggleHistory: () => void;
}> = ({ showHistory, onToggleHistory }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-3 w-3 bg-purple-500 rounded-full animate-pulse" />
            <h1 className="text-xl font-bold text-white">Image Generation</h1>
          </div>
        </div>

        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showHistory
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          title={showHistory ? 'Hide History' : 'Show History'}
        >
          <ClockIcon className="w-5 h-5" />
          <span className="text-sm font-medium">History</span>
        </button>
      </div>
    </div>
  </div>
);

const ImagePromptForm: React.FC<{
  prompt: string;
  loading: boolean;
  onPromptChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}> = ({ prompt, loading, onPromptChange, onSubmit }) => (
  <form onSubmit={onSubmit} className="mb-8">
    <div className="flex flex-col space-y-4">
      <div className="relative">
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe the image you want to generate..."
          className="w-full h-24 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-white placeholder-gray-400"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !prompt.trim()}
          className={`absolute bottom-3 right-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
            loading || !prompt.trim()
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-600 shadow-lg hover:shadow-indigo-500/25'
          }`}
        >
          {loading ? (
            <>
              <SparklesIcon className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <PhotoIcon className="w-5 h-5" />
              <span>Generate</span>
            </>
          )}
        </button>
      </div>
    </div>
  </form>
);

const ImageCard: React.FC<{
  image: GeneratedImage;
  onDelete: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}> = ({ image, onDelete, onDownload, isDownloading }) => (
  <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-transform hover:scale-[1.02] hover:shadow-xl group">
    <div className="aspect-square relative">
      <img
        src={image.url}
        alt={image.prompt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
        <button
          onClick={onDownload}
          className={`p-2 bg-blue-500/20 hover:bg-blue-500/40 rounded-full text-blue-400 hover:text-blue-300 transition-colors ${
            isDownloading ? 'animate-pulse' : ''
          }`}
          title="Download image"
          disabled={isDownloading}
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full text-red-400 hover:text-red-300 transition-colors"
          title="Delete image"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
    <div className="p-4 space-y-2">
      <p className="text-sm text-gray-300 line-clamp-2">{image.prompt}</p>
      <p className="text-xs text-gray-500">{formatDate(image.timestamp)}</p>
    </div>
  </div>
);

const HistorySidebar: React.FC<{
  show: boolean;
  images: GeneratedImage[];
  onClose: () => void;
  onDelete: (index: number) => void;
  onDownload: (url: string, index: number) => void;
  downloading: number | null;
}> = ({ show, images, onClose, onDelete, onDownload, downloading }) => (
  <div
    className={`fixed right-0 top-0 bottom-0 w-80 bg-gray-800 border-l border-gray-700 transform transition-transform duration-300 ease-in-out ${
      show ? 'translate-x-0' : 'translate-x-full'
    } z-50`}
  >
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-indigo-400" />
            History
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">{images.length} images</span>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {images.length > 0 ? (
          images.map((image, index) => (
            <div
              key={index}
              className="group relative bg-gray-700/50 rounded-lg p-3 hover:bg-gray-700/70 transition-colors"
            >
              <div className="aspect-video w-full rounded-lg overflow-hidden mb-3">
                <img
                  src={image.url}
                  alt={image.prompt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-300 line-clamp-2 mb-2">
                {image.prompt}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {formatDate(image.timestamp)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onDownload(image.url, index)}
                    className={`p-1.5 rounded-full text-gray-400 hover:text-blue-400 transition-colors ${
                      downloading === index ? 'animate-pulse text-blue-400' : ''
                    }`}
                    disabled={downloading === index}
                    title="Download image"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(index)}
                    className="p-1.5 rounded-full text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete image"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-700/50 mb-3">
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-400 text-sm">No history yet</p>
            <p className="text-gray-500 text-xs mt-1">
              Generated images will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="text-center py-12">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800 mb-4">
      <PhotoIcon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-300 mb-2">No images generated yet</h3>
    <p className="text-gray-500">Start by describing the image you want to create</p>
  </div>
);

// Utility Functions
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Main Component
const ImageGeneration: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const { setLoading: setPageLoading } = useLoading();

  useEffect(() => {
    const initializePage = async () => {
      setPageLoading(true);
      try {
        const savedImages = localStorage.getItem('generatedImages');
        if (savedImages) {
          const parsedImages = JSON.parse(savedImages).map((img: any) => ({
            ...img,
            timestamp: new Date(img.timestamp)
          }));
          setImages(parsedImages);
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Error loading saved images:', error);
      } finally {
        setPageLoading(false);
      }
    };

    initializePage();
  }, [setPageLoading]);

  useEffect(() => {
    localStorage.setItem('generatedImages', JSON.stringify(images));
  }, [images]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    setLoading(true);

    try {
      const response = await fetch(
        "https://api.nexon.my.id/api/bing/createimage?q=" + 
        encodeURIComponent(prompt.trim()) + 
        "&apikey=" + process.env.REACT_APP_NEXON_API_KEY
      );

      if (!response.ok) {
        throw new Error('Failed to generate image');
      }

      const data = await response.json();
      const result = data.data[Math.floor(Math.random() * data.data.length)]
      if (data.status && result.url) {
        const newImage: GeneratedImage = {
          url: result.url,
          prompt: prompt.trim(),
          timestamp: new Date()
        };
        setImages(prev => [newImage, ...prev]);
        setPrompt('');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url: string, index: number) => {
    try {
      setDownloading(index);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `generated-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header
        showHistory={showHistory}
        onToggleHistory={() => setShowHistory(!showHistory)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        {/* Page Title and Description */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">AI Image Generation</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Create unique and stunning images from text descriptions using our advanced AI model.
            Simply describe what you want to see, and watch your imagination come to life.
          </p>
        </div>

        {/* Image Generation Form */}
        <div className="max-w-3xl mx-auto mb-12">
          <ImagePromptForm
            prompt={prompt}
            loading={loading}
            onPromptChange={setPrompt}
            onSubmit={handleSubmit}
          />
        </div>

        {/* Generated Images Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
          {images.map((image, index) => (
            <ImageCard
              key={index}
              image={image}
              onDelete={() => handleDeleteImage(index)}
              onDownload={() => handleDownload(image.url, index)}
              isDownloading={downloading === index}
            />
          ))}
        </div>

        {images.length === 0 && !loading && <EmptyState />}
      </div>

      <HistorySidebar
        show={showHistory}
        images={images}
        onClose={() => setShowHistory(false)}
        onDelete={handleDeleteImage}
        onDownload={handleDownload}
        downloading={downloading}
      />
    </div>
  );
};

export default ImageGeneration;
